import MessageFormat from 'messageformat'
import { DEFAULT_LANGUAGE } from './constants'

/**
 * Our terms are authored in ICU in POEditor. vue-i18n v8 rendered them through a
 * `formatter`, but v9 removed that option, so the Vue 3 migration converted ICU plurals
 * to vue-i18n's `a | b` pipe syntax instead. Pipe syntax carries a single count, which
 * silently broke terms with two plural axes (STARDEV-11774).
 *
 * v9+ exposes `messageCompiler`, so hand the message to a real ICU formatter and let it
 * deal with nesting, `=N` selectors and `#`.
 */

type IMessageValues = Record<string, unknown>
type ICompiledMessage = (values: IMessageValues) => string

const formatters = new Map<string, MessageFormat>()
const compiled = new Map<string, ICompiledMessage>()

function formatterFor(locale: string): MessageFormat {
  const existing = formatters.get(locale)
  if (existing) return existing
  const formatter = new MessageFormat(locale)
  formatters.set(locale, formatter)
  return formatter
}

/**
 * A term still authored in vue-i18n's `a | b` pipe syntax. The Vue 3 migration converted the
 * ICU plurals to pipes and a handful outlived the switch back (STARDEV-11774): ICU has no
 * pipe operator, so those rendered with both branches and the separator as literal text.
 * Realms can also override terms with their own pipe-syntax copy, which no amount of fixing
 * the source terms would reach — so the branch is selected here too. No term uses `|` as
 * ordinary punctuation, which makes an unescaped pipe unambiguous.
 */
const PIPE = '|'
const ICU_SELECTOR = /\{\s*\w+\s*,\s*(?:plural|selectordinal|select)\b/

function isPipeSyntax(source: string): boolean {
  return source.includes(PIPE) && !ICU_SELECTOR.test(source)
}

// vue-i18n reads two branches as [ singular, plural ] and three as [ zero, singular, plural ].
function branchFor(branches: ICompiledMessage[], count: number | undefined): ICompiledMessage {
  if (branches.length > 2) {
    if (count === 0) return branches[0]
    return count === 1 ? branches[1] : branches[2]
  }
  return count === 1 ? branches[0] : branches[1]
}

// Depending on when the call site was written the count arrives as `count`, as `n`, or as the
// positional `{0}`, so all three are read and all three are filled back in.
function countFrom(values: IMessageValues): number | undefined {
  return [ values.count, values.n, values['0'] ]
    .find((value) => typeof value === 'number') as number | undefined
}

function compilePipe(source: string, locale: string): ICompiledMessage {
  const branches = source.split(PIPE).map((branch) => compile(branch.trim(), locale))

  return (values: IMessageValues) => {
    const count = countFrom(values)
    const withCount = count === undefined ? values : {
      0: count,
      n: count,
      count,
      ...values
    }
    return branchFor(branches, count)(withCount)
  }
}

function compile(source: string, locale: string): ICompiledMessage {
  const cacheKey = `${locale}::${source}`
  const existing = compiled.get(cacheKey)
  if (existing) return existing

  let fn: ICompiledMessage
  if (isPipeSyntax(source)) {
    fn = compilePipe(source, locale)
  } else {
    try {
      fn = formatterFor(locale).compile(source) as ICompiledMessage
    } catch {
      // A term that isn't valid ICU should render as-is rather than break the view
      fn = () => source
    }
  }
  compiled.set(cacheKey, fn)
  return fn
}

// Marks where a non-primitive value (a `<i18n-t>` slot) sat in the rendered string, so it
// can be swapped back in afterwards. NUL cannot occur in a translation.
const SLOT_MARK = '\u0000'

/**
 * `<i18n-t>` renders slots as VNodes and needs the message back as a list of parts, not a
 * joined string — returning a string makes vue-i18n throw "Unexpected return type in
 * composer" and the element renders empty (STARDEV-11792).
 *
 * ICU formatters only produce strings, so each VNode value is rendered as a placeholder
 * and spliced back out. Primitives are passed through untouched, so plural selection and
 * `#` still see the real count.
 */
function renderParts(
  fn: ICompiledMessage,
  values: IMessageValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ctx: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const slots: unknown[] = []
  const marked: IMessageValues = {}

  Object.keys(values).forEach((key) => {
    const value = values[key]
    if (value !== null && typeof value === 'object') {
      marked[key] = `${SLOT_MARK}${slots.length}${SLOT_MARK}`
      slots.push(value)
    } else {
      marked[key] = value
    }
  })

  // Each slot contributes exactly two marks, so splitting on the mark interleaves literals
  // and slot indices: even positions are text, odd positions are an index into `slots`.
  const parts = fn(marked)
    .split(SLOT_MARK)
    .map((part, index) => (index % 2 === 1 ? slots[Number(part)] : part))
    .filter((part) => part !== '')

  return ctx.normalize(parts)
}

/**
 * vue-i18n's `messageCompiler` hook. Its message/context types are internal, so they are
 * loosely typed here — the contract is (message, context) => (ctx) => rendered message.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function compileIcuMessage(message: any, context: any): any {
  const source = typeof message === 'string' ? message : String(message ?? '')
  const locale: string = context?.locale || DEFAULT_LANGUAGE
  const fn = compile(source, locale)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (ctx: any): any => {
    const values: IMessageValues = ctx?.values ?? {}
    try {
      if (ctx?.type === 'vnode') {
        return renderParts(fn, values, ctx)
      }
      return fn(values)
    } catch {
      // Missing or mistyped interpolation values must not blank out the UI
      return ctx?.type === 'vnode' ? ctx.normalize([ source ]) : source
    }
  }
}
